import React, { useState, useMemo } from 'react';
import { Search, Filter, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { reports, industries } from '../mock';

const Reports = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const matchesSearch = report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           report.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesIndustry = selectedIndustry === 'all' || report.industrySlug === selectedIndustry;
      return matchesSearch && matchesIndustry;
    });
  }, [searchTerm, selectedIndustry]);

  if (selectedReport) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-amber-50 py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => setSelectedReport(null)} 
            variant="ghost" 
            className="mb-6 text-[#A0704E] hover:text-[#8B5F3E]"
          >
            ← Back to Reports
          </Button>
          <article className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <div className="flex flex-wrap gap-3 mb-6">
              <Badge className="bg-[#A0704E] text-white">{selectedReport.industry}</Badge>
              <Badge variant="outline" className="border-gray-300">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(selectedReport.date).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Badge>
              <Badge variant="outline" className="border-gray-300">
                <Clock className="w-3 h-3 mr-1" />
                {selectedReport.readTime}
              </Badge>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">{selectedReport.title}</h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">{selectedReport.excerpt}</p>
            
            <div className="border-l-4 border-[#A0704E] pl-6 py-4 mb-8 bg-amber-50">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Insights</h2>
              <ul className="space-y-3">
                {selectedReport.keyInsights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-[#A0704E] rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding the Australian Market Context</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                The Australian market presents unique opportunities and challenges for brands entering or expanding within this space. 
                This report analyzes the critical touchpoints that influence consumer decision-making and brand perception in the {selectedReport.industry} sector.
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Journey Mapping</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Through comprehensive research combining student insights and market analysis, we've identified the key moments 
                that matter most to Australian consumers. Each touchpoint represents an opportunity to strengthen brand connection 
                and drive meaningful engagement.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3">Practical Applications</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                These insights can be directly applied to marketing projects, IMC campaigns, and real-world business strategies. 
                Whether you're a student working on a university project or a business planning market entry, understanding these 
                touchpoints will guide your strategic decisions.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-3">Methodology</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                This report combines primary research from marketing students across Australian universities with secondary analysis 
                of market trends, consumer behavior data, and successful campaign case studies. Our unique approach brings fresh 
                perspectives while maintaining research rigor.
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                This report is part of Marketry's bi-weekly industry series. Subscribe to receive future insights directly to your inbox.
              </p>
            </div>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-amber-50 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Industry Reports</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our bi-weekly reports covering different industries in the Australian market
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-10">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search reports by title or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-6 text-lg border-2 border-gray-200 focus:border-[#A0704E]"
              />
            </div>
          </div>

          {/* Industry Filter Chips */}
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="w-5 h-5 text-gray-500" />
            <Button
              onClick={() => setSelectedIndustry('all')}
              variant={selectedIndustry === 'all' ? 'default' : 'outline'}
              className={selectedIndustry === 'all' ? 'bg-[#A0704E] hover:bg-[#8B5F3E]' : 'border-gray-300 hover:border-[#A0704E]'}
            >
              All Industries
            </Button>
            {industries.map((industry) => (
              <Button
                key={industry.id}
                onClick={() => setSelectedIndustry(industry.slug)}
                variant={selectedIndustry === industry.slug ? 'default' : 'outline'}
                className={selectedIndustry === industry.slug ? 'bg-[#A0704E] hover:bg-[#8B5F3E]' : 'border-gray-300 hover:border-[#A0704E]'}
              >
                {industry.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-[#A0704E]">{filteredReports.length}</span> {filteredReports.length === 1 ? 'report' : 'reports'}
          </p>
        </div>

        {/* Reports Grid */}
        {filteredReports.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReports.map((report) => (
              <Card 
                key={report.id} 
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-2 border-gray-100 hover:border-[#A0704E]"
                onClick={() => setSelectedReport(report)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-amber-100 text-[#A0704E] hover:bg-amber-200">
                      {report.industry}
                    </Badge>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(report.date).toLocaleDateString('en-AU')}
                    </span>
                  </div>
                  <CardTitle className="text-xl leading-tight hover:text-[#A0704E] transition-colors">
                    {report.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 mb-4 leading-relaxed">
                    {report.excerpt}
                  </CardDescription>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {report.readTime}
                    </span>
                    <span className="text-sm font-medium text-[#A0704E]">Read More →</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No reports found matching your criteria</p>
            <Button 
              onClick={() => { setSearchTerm(''); setSelectedIndustry('all'); }}
              className="mt-6 bg-[#A0704E] hover:bg-[#8B5F3E]"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;